import { z } from 'zod';

/**
 * A failure in the browser, reported so it reaches the server log (docs/debugging/design.md). The
 * server logs it with the reporter's verified user and organization; nothing is stored. The page
 * sends no text it shows: the message and stack of the error only, the path without its query.
 *
 * - `error`: an uncaught exception.
 * - `rejection`: an unhandled promise rejection that is not an API or network failure.
 * - `chunk`: a part of the app could not be loaded (often an old version after a deploy).
 * - `contract`: an API answer that does not match the shared contract.
 */
export const CLIENT_ERROR_KINDS = ['error', 'rejection', 'chunk', 'contract'] as const;
export type ClientErrorKind = (typeof CLIENT_ERROR_KINDS)[number];

export const CLIENT_ERROR_MESSAGE_MAX = 500;
export const CLIENT_ERROR_STACK_MAX = 4000;

export const ClientErrorReportSchema = z.object({
  kind: z.enum(CLIENT_ERROR_KINDS),
  message: z.string().max(CLIENT_ERROR_MESSAGE_MAX),
  stack: z.string().max(CLIENT_ERROR_STACK_MAX).optional(),
  /** The page's path, without query or hash. */
  path: z.string().max(300),
  /** The web build (the commit it was built from, or "dev"). */
  release: z.string().max(64),
  /** The `traceId` of the API answer the failure followed, when there was one. */
  traceId: z.uuid().optional(),
});
export type ClientErrorReport = z.infer<typeof ClientErrorReportSchema>;
